<script>
//
import { fandomBasicMixin, fandomExtendedMixin } from "src/modules/fandom_mixins_module.js";
import { VodafoneLab } from "../../modules/vodafone_lab_module.js";
import { get } from "lodash";

export default {
  mixins: [fandomBasicMixin, fandomExtendedMixin],
  props: {
    alternativeAppendContents: {
      type: Function,
      required: false
    },
    favouriteInteractions: {
      type: Boolean,
      default: false
    },
    appendContentsParams: {
      type: Object,
      default: () => { return {} }
    },
    tileSize: {
      type: String,
      default: null
    },
    tileLayout: {
      type: String,
      default: null
    }
  },
  methods: {
    appendContents() {
      VodafoneLab.addDigitalDataEvent("search_results_clicktrough", "search", ":Search apply");

      if (!!this.alternativeAppendContents) {
        this.alternativeAppendContents();
      } else {
        const params = {
          ...this.appendContentsParams,
          exclude_content_ids: this.content.children.map(name => this.getContent(name).id)
        }
        Fandom.appendContents(this.content.name, null, (this.content.limit || 8), params);
      }
    },
    childLayout(child) {
      return this.getContent(child).layout;
    },
    isotopeClass(child) {
      const size = this.tileSize || this.getContent(child).isotope_size || 'widecontent';
      return `item--${size}`;
    }
  },
  computed: {
    paginationSEOUrl() {
      const paginationData = {
        page_content_name: Fandom.getUrlParameter("page_content_name"),
        page: parseInt(Fandom.getUrlParameter("page")) || 1
      };

      if (paginationData.page_content_name == this.content.name) {
        return window.location.pathname + `?page_content_name=${paginationData.page_content_name}&page=${paginationData.page + 1}`; 
      } else {
        return window.location.pathname + `?page_content_name=${this.content.name}&page=2`;
      }
    },
    colors() {
      return {
        "--content-background": get(this, ["content", "background_color"], null),
        "--content-color": get(this, ["content", "text_color"], null)
      };
    },
    themeType() {
      const decoratorThemeType = get(this, ["decorator", "theme"], "light"); 
      return this.inheritThemeType || this.content.theme || decoratorThemeType;
    },
    breakpointIndex() {
      if (!!this.stripes && this.stripes.length > 0) {
        return this.content.stripes_index || 3;
      } else {
        return false;
      }
    },
    children() {
      if (this.breakpointIndex) {
        return (this.content.children || []).slice(0,this.breakpointIndex);
      } else {
        return this.content.children;
      }
    },
    otherChildren() {
       if (this.breakpointIndex) {
        return (this.content.children || []).slice(this.breakpointIndex, this.content.children.length);
      } else {
        return false;
      }
    },
    stripes() {
      const stripes = get(this.content, 'stripes', []).filter(stripe => {
        if (Fandom.isUserRegistered()) {
          return !stripe.hide_to_registered_user;
        } else {
          return !stripe.hide_to_anonymous_user;
        }
      });
      return stripes.length > 0 ? stripes : false;
    },
    tileVertical() {
      return this.content.tile_vertical || false;
    },
    gridStyle() {
      return {
        'grid-template-columns': "1fr 1fr ".repeat(parseInt(this.content.column_count) || 2),
      }
    },
    gridClass() {
      return this.hasUltrawideFirstTile && this.content.highlight_first_tile ? 'grid-highlight-first-tile' : ''
    },
    hasUltrawideFirstTile() {
      return this.getContent(this.content.children[0]).isotope_size === 'ultrawidecontent';
    }
  },
};

</script>

<template>
  <div
    v-if="isContentVisibleToUser()"
    v-easyadmin="easyadminId"
    grid-isotope-component
    :class="[contentType]"
    class="py-4 py-md-5"
    :id="anchorId"
    :style="[theme, colors]"
  >
    <div class="container">
      <div v-if="!content.children || content.children.length == 0" class="w-100 d-flex justify-content-center">
        <p class="text-uppercase text-muted">{{ft("globals.no_content")}}</p>
      </div>
      <div class="row" v-else>
        <div class="col-12 px-sm-0 grid" :style="gridStyle" :class="gridClass">
          <component
            :is="tileLayout || childLayout(child)"
            :content="getContent(child)"
            v-for="child in children"
            :key="`item-${getContent(child).name}`"
            :class="isotopeClass(child)"
            class="item"
            :showFavouriteTrigger="favouriteInteractions"
            :vertical="tileVertical"
          ></component>
        </div>
      </div>
    </div>
    <component
      :is="stripe.layout"
      :content="stripe"
      v-for="stripe in stripes"
      :key="stripe.$id"
      :opt-container-content="containerContent"
    ></component>
    <div class="container" v-if="otherChildren">
      <div class="row">
        <div class="col-12 px-sm-0 grid" :class="gridClass" :style="gridStyle">
          <component
            :is="tileLayout || childLayout(child)"
            :content="getContent(child)"
            v-for="child in otherChildren"
            :key="`item-${getContent(child).name}`"
            :class="isotopeClass(child)"
            class="item"
            :showFavouriteTrigger="favouriteInteractions"
            :vertical="tileVertical"
          ></component>
        </div>
      </div>
    </div>
    <div v-if="content.button" class="container mt-4 mt-md-5">
      <div class="row">
        <div class="col-sm-12 text-center">
          <a :href="content.button.url" :target="getTarget(content.button)" class="btn shadow-none" :class="themeButtonClass">{{content.button.title}}</a>
        </div>
      </div>
    </div>
    <div v-if="content.has_more_children" class="container mt-4 mt-md-5">
      <div class="row">
        <div class="col-sm-12 text-center">
          <a :href="paginationSEOUrl" class="btn shadow-none" :class="themeButtonClass" @click.prevent="appendContents()">{{ft("globals.load_more")}}</a>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
[grid-isotope-component] {
  color: var(--content-color);
  background-color: var(--content-background);

  @include media-breakpoint-up(md) {
    .grid {
      &-highlight-first-tile {
        grid-template-rows: 25rem 1fr;
      }
    }
  }

  .grid {
    grid-auto-flow: row dense;
    grid-auto-rows: 1fr;
    grid-gap: 1.5rem;
    display: grid;

    .item--content {
      grid-row: span 1;
      grid-column: span 1;
    }

    .item--widecontent {
      grid-row: span 1;
      grid-column: span 2;

      @include media-breakpoint-down(sm) {
        position: relative;
        padding-bottom: 135%;
        
        & > div {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
        }
      }
    }

    .item--ultrawidecontent {
      grid-row: span 1;
      grid-column: span 4;
    }

    .item--largesquarecontent {
      grid-row: span 2;
      grid-column: span 2;
      position: relative;
      & > div {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }

      @include media-breakpoint-up(md) {
        padding-bottom: 100%;
      }

      @include media-breakpoint-down(sm) {
        padding-bottom: 135%;
      }
    }
  }

  @include media-breakpoint-down(sm) {
    .grid {
      grid-template-columns: 1fr 1fr !important;

      &-first-ultrawide {
        grid-template-rows: none;
      }

      .item--widecontent,
      .item--ultrawidecontent,
      .item--largesquarecontent {
        grid-row: span 2;
        grid-column: span 2;
      }
    }
  }
}
</style>
